import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import dayjs from 'dayjs'
import {
  ForecastData,
  ForecastDay,
  ManagerSetupState,
  PrepItem,
  PrepSubItem,
  SchedulePrepData,
  UptData
} from 'src/types/apps/managerSetup'

const DEFAULT_AM_PERCENT = 41
const SEED_FORECASTS = [15000, 14780, 16450, 18600, 21000, 22600, 20760]

// How many weeks ahead we allow (until end of current month)
export function maxWeekOffset(): number {
  const today = dayjs()
  const endOfMonth = today.endOf('month')
  let offset = 0
  while (true) {
    const nextMonday = today.startOf('week').add(1 + 7 * (offset + 1), 'day')
    if (nextMonday.isAfter(endOfMonth)) break
    offset++
  }

  return offset
}

function buildWeekDays(weekOffset = 0): ForecastDay[] {
  const monday = dayjs()
    .startOf('week')
    .add(1 + 7 * weekOffset, 'day')

  return SEED_FORECASTS.map((ctForecast, i) => {
    const date = monday.add(i, 'day')

    return {
      date: date.format('YYYY-MM-DD'),
      day: date.format('dddd D MMM'),
      ctForecast,
      amPercent: DEFAULT_AM_PERCENT
    }
  })
}

// Normalize API response (dayDate/dayLabel) → frontend shape (date/day)
function normalizeForecastDays(days: any[]): ForecastDay[] {
  return days.map(d => ({
    date: d.date ?? d.dayDate?.substring(0, 10) ?? '',
    day: d.day ?? d.dayLabel ?? '',
    ctForecast: d.ctForecast ?? 0,
    managerOverride: d.managerOverride ?? undefined,
    amPercent: d.amPercent ?? DEFAULT_AM_PERCENT
  }))
}

// ─── Reference data (CT seed) ────────────────────────────────────────────────
// Tries the backend API first; falls back to local mock on failure so the UI
// works while the backend is not yet deployed / connected.

export const fetchReferenceData = createAsyncThunk(
  'managerSetup/fetchReferenceData',
  async ({ restaurantId, weekOffset = 0 }: { restaurantId: string; weekOffset?: number }, { extra }: any) => {
    const monday = dayjs()
      .startOf('week')
      .add(1 + 7 * weekOffset, 'day')
    const weekOf = monday.format('D MMM')
    const ctSyncedAt = `Today, ${dayjs().format('h:mm A')}`

    try {
      const { data } = await extra.httpClient.get(`/manager-prep/restaurant/${restaurantId}/reference-data`)
      const payload = data.data

      // Normalize API field names (dayDate/dayLabel → date/day)
      return {
        ...payload,
        forecastData: {
          ...payload.forecastData,
          days: normalizeForecastDays(payload.forecastData?.days ?? [])
        }
      } as {
        weekOf: string
        ctSyncedAt: string
        forecastData: ForecastData
        uptData: UptData
        schedulePrepData: SchedulePrepData
      }
    } catch {
      // Backend not yet live — serve from local seed
      return {
        weekOf,
        ctSyncedAt,
        forecastData: { weekOf, ctSyncedAt, days: buildWeekDays(weekOffset) } as ForecastData,
        uptData: {
          weekOf,
          ctSyncedAt,
          sections: [
            {
              name: 'Protein',
              items: [
                { id: 'grilled-chicken', item: 'Grilled Chicken', theoreticalUpt: 3.5, actualUpt: 3.4 },
                { id: 'ground-beef', item: 'Ground Beef', theoreticalUpt: 3.4, actualUpt: 3.6 },
                { id: 'shredded-beef', item: 'Shredded Beef', theoreticalUpt: 2.75, actualUpt: 3.1 },
                { id: 'chorizo', item: 'Chorizo', theoreticalUpt: 1.5, actualUpt: 1.5 }
              ]
            },
            {
              name: 'Fresh Prep',
              items: [
                { id: 'pico', item: 'Pico', theoreticalUpt: 0.9, actualUpt: 0.95 },
                { id: 'avocado', item: 'Avocado', theoreticalUpt: 1.75, actualUpt: 1.75 },
                { id: 'carrots', item: 'Carrots', theoreticalUpt: 0.25, actualUpt: 0.45 }
              ]
            }
          ]
        } as UptData,
        schedulePrepData: {
          weekOf,
          ctSyncedAt,
          sections: [
            {
              name: 'Cook Prep',
              items: [
                {
                  id: 'prep-fresh-once',
                  item: 'Prep Fresh — Once Daily',
                  hasAmPrep: true,
                  hasPmPrep: false,
                  hasCombinePrep: false,
                  subItems: [
                    {
                      id: 'avocado-mix',
                      item: 'Avocado Mix',
                      hasAmPrep: true,
                      hasPmPrep: false,
                      hasCombinePrep: false
                    },
                    {
                      id: 'orange-pulp',
                      item: 'Orange Pulp',
                      hasAmPrep: true,
                      hasPmPrep: false,
                      hasCombinePrep: false
                    },
                    {
                      id: 'pineapple-slices',
                      item: 'Pineapple Slices',
                      hasAmPrep: true,
                      hasPmPrep: false,
                      hasCombinePrep: true
                    }
                  ]
                },
                {
                  id: 'prep-fresh-twice',
                  item: 'Prep Fresh — Twice Daily',
                  hasAmPrep: true,
                  hasPmPrep: true,
                  hasCombinePrep: false,
                  subItems: [
                    { id: 'guacamole', item: 'Guacamole', hasAmPrep: true, hasPmPrep: true, hasCombinePrep: true },
                    {
                      id: 'pico-de-gallo',
                      item: 'Pico de Gallo',
                      hasAmPrep: true,
                      hasPmPrep: true,
                      hasCombinePrep: false
                    },
                    {
                      id: 'sour-cream-mix',
                      item: 'Sour Cream Mix',
                      hasAmPrep: true,
                      hasPmPrep: true,
                      hasCombinePrep: false
                    }
                  ]
                },
                {
                  id: 'prep-in-advance',
                  item: 'Prep in Advance',
                  hasAmPrep: true,
                  hasPmPrep: true,
                  hasCombinePrep: true,
                  subItems: [
                    {
                      id: 'chipotle-sauce',
                      item: 'Chipotle Sauce',
                      hasAmPrep: true,
                      hasPmPrep: false,
                      hasCombinePrep: false
                    },
                    { id: 'mild-salsa', item: 'Mild Salsa', hasAmPrep: true, hasPmPrep: false, hasCombinePrep: true }
                  ]
                },
                {
                  id: 'cook-hotline',
                  item: 'Cook Hotline Items',
                  hasAmPrep: false,
                  hasPmPrep: false,
                  hasCombinePrep: false,
                  note: 'Cook times based on demand and holding time'
                },
                {
                  id: 'req-tomorrow',
                  item: 'Requirement for Tomorrow',
                  hasAmPrep: true,
                  hasPmPrep: true,
                  hasCombinePrep: false,
                  subItems: [
                    {
                      id: 'protein-thaw',
                      item: 'Protein Thaw',
                      hasAmPrep: true,
                      hasPmPrep: false,
                      hasCombinePrep: false
                    },
                    { id: 'marinade', item: 'Marinade', hasAmPrep: true, hasPmPrep: false, hasCombinePrep: false }
                  ]
                }
              ]
            },
            {
              name: 'Line Prep',
              items: [
                {
                  id: 'tortillas-churros',
                  item: 'Tortillas & Churros',
                  hasAmPrep: true,
                  hasPmPrep: true,
                  hasCombinePrep: true,
                  subItems: [
                    {
                      id: 'flour-tortillas',
                      item: 'Flour Tortillas',
                      hasAmPrep: true,
                      hasPmPrep: true,
                      hasCombinePrep: false
                    },
                    {
                      id: 'churro-dough',
                      item: 'Churro Dough',
                      hasAmPrep: true,
                      hasPmPrep: false,
                      hasCombinePrep: false
                    }
                  ]
                },
                {
                  id: 'fryer-prep',
                  item: 'Fryer Prep',
                  hasAmPrep: true,
                  hasPmPrep: false,
                  hasCombinePrep: true,
                  subItems: [
                    {
                      id: 'nacho-chips',
                      item: 'Nacho Chips',
                      hasAmPrep: true,
                      hasPmPrep: false,
                      hasCombinePrep: false
                    },
                    { id: 'taco-shells', item: 'Taco Shells', hasAmPrep: true, hasPmPrep: false, hasCombinePrep: false }
                  ]
                },
                {
                  id: 'cold-line-prep',
                  item: 'Cold Line Prep',
                  hasAmPrep: true,
                  hasPmPrep: true,
                  hasCombinePrep: true,
                  subItems: [
                    {
                      id: 'shredded-lettuce',
                      item: 'Shredded Lettuce',
                      hasAmPrep: true,
                      hasPmPrep: true,
                      hasCombinePrep: false
                    },
                    {
                      id: 'diced-tomato',
                      item: 'Diced Tomato',
                      hasAmPrep: true,
                      hasPmPrep: true,
                      hasCombinePrep: false
                    },
                    {
                      id: 'cheese-blend',
                      item: 'Cheese Blend',
                      hasAmPrep: true,
                      hasPmPrep: false,
                      hasCombinePrep: true
                    }
                  ]
                }
              ]
            }
          ]
        } as SchedulePrepData
      }
    }
  }
)

// ─── Local draft helpers ──────────────────────────────────────────────────────
const lsKey = (restaurantId: string) => `manager-prep-draft-${restaurantId}`

function saveDraftLocally(restaurantId: string, payload: any) {
  try {
    localStorage.setItem(lsKey(restaurantId), JSON.stringify({ ...payload, savedAt: new Date().toISOString() }))
  } catch {
    /* storage quota */
  }
}

function loadDraftLocally(restaurantId: string) {
  try {
    const raw = localStorage.getItem(lsKey(restaurantId))

    return raw ? JSON.parse(raw) : null
  } catch {
    return null
  }
}

// ─── Load existing draft for current week (if any) ───────────────────────────

export const fetchCurrentWeekSubmission = createAsyncThunk(
  'managerSetup/fetchCurrentWeekSubmission',
  async (restaurantId: string, { extra }: any) => {
    try {
      const { data } = await extra.httpClient.get(`/manager-prep/restaurant/${restaurantId}/submissions/current-week`)

      return data.data
    } catch {
      // Backend not live — fall back to localStorage draft
      return loadDraftLocally(restaurantId)
    }
  }
)

// ─── Save (draft or submit) ───────────────────────────────────────────────────

export const saveManagerPrep = createAsyncThunk(
  'managerSetup/saveManagerPrep',
  async (
    {
      restaurantId,
      payload
    }: {
      restaurantId: string
      payload: {
        weekStartDate: string
        status?: 'draft' | 'submitted'
        forecastDays: any[]
        uptItems: any[]
        scheduleItems: any[]
      }
    },
    { extra }: any
  ) => {
    // Always persist locally so data survives page refresh
    saveDraftLocally(restaurantId, payload)

    try {
      const { data } = await extra.httpClient.post(`/manager-prep/restaurant/${restaurantId}/submissions`, payload)

      return data.data
    } catch {
      // Backend not yet live — local save already done above
      return null
    }
  }
)

// ─── Slice ────────────────────────────────────────────────────────────────────

const initialState: ManagerSetupState = {
  activeStep: 0,
  weekOffset: 0,
  forecastData: null,
  uptData: null,
  schedulePrepData: null,
  loading: false,
  saving: false,
  errors: null,
  savedSteps: [false, false, false]
}

const managerSetupSlice = createSlice({
  name: 'managerSetup',
  initialState,
  reducers: {
    setActiveStep(state, action: PayloadAction<number>) {
      state.activeStep = action.payload
    },
    setWeekOffset(state, action: PayloadAction<number>) {
      state.weekOffset = action.payload

      // Rebuild forecast days for the new week using seed data
      const monday = dayjs()
        .startOf('week')
        .add(1 + 7 * action.payload, 'day')
      const weekOf = monday.format('D MMM')
      if (state.forecastData) {
        state.forecastData.weekOf = weekOf
        state.forecastData.days = buildWeekDays(action.payload)
      }
    },
    updateForecastDay(
      state,
      action: PayloadAction<{ index: number; field: 'managerOverride' | 'amPercent'; value: number | undefined }>
    ) {
      if (!state.forecastData) return
      const { index, field, value } = action.payload
      state.forecastData.days[index][field] = value as number
    },
    updateUptOverride(state, action: PayloadAction<{ sectionIndex: number; itemIndex: number; value?: number }>) {
      if (!state.uptData) return
      const { sectionIndex, itemIndex, value } = action.payload
      state.uptData.sections[sectionIndex].items[itemIndex].override = value
    },
    updatePrepItem(
      state,
      action: PayloadAction<{
        sectionIndex: number
        itemIndex: number
        field: keyof PrepItem
        value: string | boolean | undefined
      }>
    ) {
      if (!state.schedulePrepData) return
      const { sectionIndex, itemIndex, field, value } = action.payload
      ;(state.schedulePrepData.sections[sectionIndex].items[itemIndex] as any)[field] = value
    },
    updatePrepSubItem(
      state,
      action: PayloadAction<{
        sectionIndex: number
        itemIndex: number
        subItemIndex: number
        field: keyof PrepSubItem
        value: string | boolean | undefined
      }>
    ) {
      if (!state.schedulePrepData) return
      const { sectionIndex, itemIndex, subItemIndex, field, value } = action.payload
      const subItems = state.schedulePrepData.sections[sectionIndex].items[itemIndex].subItems
      if (subItems) (subItems[subItemIndex] as any)[field] = value
    },
    applyPrepTimeToSubItems(
      state,
      action: PayloadAction<{
        sectionIndex: number
        itemIndex: number
        field: 'amPrepTime' | 'pmPrepTime'
        value: string
      }>
    ) {
      if (!state.schedulePrepData) return
      const { sectionIndex, itemIndex, field, value } = action.payload
      const item = state.schedulePrepData.sections[sectionIndex].items[itemIndex]
      item[field] = value
      item.subItems?.forEach(sub => {
        sub[field] = value
      })
    },
    markStepSaved(state, action: PayloadAction<number>) {
      state.savedSteps[action.payload] = true
    },
    resetManagerSetup(state) {
      state.activeStep = 0
      state.weekOffset = 0
      state.savedSteps = [false, false, false]
      state.errors = null
      state.forecastData = null
      state.uptData = null
      state.schedulePrepData = null
    }
  },
  extraReducers: builder => {
    // fetchReferenceData
    builder
      .addCase(fetchReferenceData.pending, state => {
        state.loading = true
      })
      .addCase(fetchReferenceData.fulfilled, (state, action) => {
        state.loading = false
        state.forecastData = action.payload.forecastData
        state.uptData = action.payload.uptData
        state.schedulePrepData = action.payload.schedulePrepData
      })
      .addCase(fetchReferenceData.rejected, (state, action) => {
        state.loading = false
        state.errors = action.error
      })

    // fetchCurrentWeekSubmission — hydrate saved values on top of reference data
    builder
      .addCase(fetchCurrentWeekSubmission.pending, state => {
        state.loading = true
      })
      .addCase(fetchCurrentWeekSubmission.fulfilled, (state, action) => {
        state.loading = false
        const submission = action.payload
        if (!submission) return

        // Hydrate manager overrides and AM% from saved draft
        if (state.forecastData && submission.forecastDays?.length) {
          submission.forecastDays.forEach((saved: any) => {
            const idx = state.forecastData!.days.findIndex(d => d.date === saved.dayDate?.substring(0, 10))
            if (idx >= 0) {
              state.forecastData!.days[idx].managerOverride = saved.managerOverride
              state.forecastData!.days[idx].amPercent = saved.amPercent
            }
          })
        }

        if (state.uptData && submission.uptItems?.length) {
          submission.uptItems.forEach((saved: any) => {
            state.uptData!.sections.forEach(section => {
              if (section.name === saved.sectionName) {
                const item = section.items.find(i => i.id === saved.itemKey)
                if (item) item.override = saved.managerOverride
              }
            })
          })
        }

        if (state.schedulePrepData && submission.scheduleItems?.length) {
          submission.scheduleItems.forEach((saved: any) => {
            state.schedulePrepData!.sections.forEach(section => {
              if (section.name === saved.sectionName) {
                const item = section.items.find(i => i.id === saved.itemKey)
                if (item) {
                  item.amPrepTime = saved.amPrepTime
                  item.pmPrepTime = saved.pmPrepTime
                  item.combinePrep = saved.combinePrep
                  item.combinePrepTime = saved.combinePrepTime
                }
              }
            })
          })
        }
      })
      .addCase(fetchCurrentWeekSubmission.rejected, (state, action) => {
        state.loading = false
        state.errors = action.error
      })

    // saveManagerPrep
    builder
      .addCase(saveManagerPrep.pending, state => {
        state.saving = true
      })
      .addCase(saveManagerPrep.fulfilled, state => {
        state.saving = false
      })
      .addCase(saveManagerPrep.rejected, (state, action) => {
        state.saving = false
        state.errors = action.error
      })
  }
})

export const {
  setActiveStep,
  setWeekOffset,
  updateForecastDay,
  updateUptOverride,
  updatePrepItem,
  updatePrepSubItem,
  applyPrepTimeToSubItems,
  markStepSaved,
  resetManagerSetup
} = managerSetupSlice.actions

export default managerSetupSlice.reducer
